import React, { useEffect, useRef, useState } from 'react';
import NightsStepper from '../../../components/NightsStepper';
import PlannedPill from './PlannedPill';
import { getTransportEmoji, getModeLabel, getEmissionLevel, EMISSION_LEVEL_COLOR, EMISSION_LEVEL_LABEL, NIGHT_TRAIN_EMISSIONS_NOTE, isKnownTransportMode, isAirplaneMode, isNightTrainMode, FLIGHT_TIME_TOOLTIP, TRANSPORT_MODE_OPTIONS } from '../../../shared/transport';
import Dropdown from '../../../components/ui/Dropdown';
import KebabMenu, { KebabIcons } from './KebabMenu';
import { getCountryFlag } from '../../../shared/countryFlag';
import { cityOnly, coerceDecimal, formatDurationMin, formatDurationExact, formatEuroExact, currentModeOverride, computeJourneySchedule, hoursToNights, legNightsOnBoard, stopSequenceLabels, StopSequenceLabel } from '../utils';
import type { LegNightTrain, NightTrainSearchSummary } from '../../journeys/api';
import { NEARBY_SEARCH_LABEL, NEARBY_SEARCH_OTHER_LABEL, NightTrainStatus, bookingLabel, canSearchNearby, cardRunsNote, modeOptionsForLeg, nightTrainChip, productLine, replacesLabel, rideNote, searchBlockedReason, timesOnly } from '../nightTrain';
import { TrustMark } from './NightTrainSheetBlock';
import NightTrainSearchButton from './NightTrainSearchButton';
import StopPlacePicker, { type PreviewPlaceFn, type SuggestPlacesFn } from './StopPlacePicker';
import type { StopSuggestionPick } from '../../journeys/api';
import { CityResult } from '../../geocoding/api';
import Tooltip from '../../../components/ui/Tooltip';

// Vertical route timeline restyled to the prototype (jody-journey-v2.html §route):
// a 30px rail (green-soft numbered circle + dashed connector line) with each stop
// and leg as a white bordered card, and a "+" add-here on the rail between stops.
// Stops edit inline (hover → pencil/trash); a LEG opens its detail sheet
// (LegSheet) on tap — mode changes moved there. Async price/description show an
// animated "•••" loader (min 3s) until they land.

export interface TimelineEditHandlers {
  busy?: boolean;
  /** `suggestion` is set when the place came from "Describe a place". */
  onAddStop?: (afterStopId: string | null, city: CityResult, suggestion?: StopSuggestionPick) => void;
  onRemoveStop?: (stopId: string) => void;
  onReplaceStop?: (stopId: string, city: CityResult, suggestion?: StopSuggestionPick) => void;
  /** Offers "Describe a place" in the add/replace picker (StopPlacePicker). */
  onSuggestPlaces?: SuggestPlacesFn;
  /** Hovering a suggested place previews it on the page's map. */
  onPreviewPlace?: PreviewPlaceFn;
  /** Open the leg detail sheet (replaces the old inline mode editor). */
  /** `source` tells analytics whether the sheet opened from the card body or
   *  the Planned pill (leg_details_viewed: card_tap | pill_tap). */
  onOpenLeg?: (legId: string, source?: 'card' | 'pill') => void;
  /** Open the stop detail sheet (stay details + Booking CTA). When provided,
   *  the stop's Planned pill opens the sheet instead of toggling in place —
   *  same interaction as the leg pill (prototype §7). */
  onOpenStop?: (stopId: string, source?: 'card' | 'pill') => void;
  /** Open the Booking.com stay search for a stop (unplanned stays; the stop
   *  twin of onOmioLeg). */
  onBookingStop?: (stopId: string) => void;
  /** Live nights edits from the card steppers. Pages that build stay links
   *  from journey state MUST provide this and patch the stop's hours, or a
   *  fresh stepper tick won't reach the Booking link's dates. */
  onNightsChange?: (stopId: string, nights: number) => void;
  /** A nights change was saved (the stepper saves itself, after a pause). */
  onNightsSaved?: (stopId: string) => void;
  /** Toggle the Planned/Unplanned status pill (stay for a stop, ticket for a leg). */
  onToggleStopPlanned?: (stopId: string, planned: boolean) => void;
  onToggleLegPlanned?: (legId: string, planned: boolean) => void;
}

interface TimelineStop {
  id: string;
  order?: number;
  /** Only to tell a round trip's End (stopSequenceLabels). */
  latitude?: number | string | null;
  longitude?: number | string | null;
  city?: string;
  location?: string;
  country?: string;
  description?: string | null;
  activities?: string[];
  things_to_do?: string[];
  stopDurationHours?: number | null;
  planned?: boolean;
  stayDetails?: { placeName?: string | null; costCents?: number | null } | null;
}

interface TimelineLeg {
  id: string;
  order?: number;
  fromStopId?: string;
  toStopId?: string;
  transportMode?: string | null;
  durationHours?: number | string | null;
  durationMinutes?: number | string | null;
  distanceKm?: number | string | null;
  costCents?: number | string | null;
  priceBandLowCents?: number | string | null;
  priceBandHighCents?: number | string | null;
  co2eKg?: number | string | null;
  planned?: boolean;
  userOverrides?: Record<string, { costCents?: number; durationMinutes?: number }> | null;
  ticketDetails?: { date?: string | null; time?: string | null; extraInfos?: string | null } | null;
  /** Night-train legs: the train (docs/product/night-trains/README.md). */
  nightTrain?: LegNightTrain | null;
  /** The timetable data has a night train for this leg, so it can be offered. */
  nightTrainAvailable?: boolean;
  /** Why not, when it cannot: the picker shows the mode disabled, with the reason. */
  nightTrainStatus?: NightTrainStatus | null;
  nightTrainNote?: string | null;
  /** What "Search night trains" would find: where it finds nothing the card says so, and does not offer it. */
  nightTrainSearch?: NightTrainSearchSummary | null;
}

export interface RouteTimelineProps extends TimelineEditHandlers {
  stops: TimelineStop[];
  legs: TimelineLeg[];
  departureDate?: string | null;
  returnDate?: string | null;
  /** Open the Omio affiliate link for a leg (unplanned, Omio-covered modes). */
  onOmioLeg?: (legId: string) => void;
  /** Open where a night-train leg is booked (operator site or Omio). */
  onNightTrainBooking?: (legId: string) => void;
  /** Quick transport switch straight from a leg card (no sheet round-trip). */
  onChangeLegMode?: (legId: string, mode: string) => void;
  /** Leg id whose Omio link is currently being fetched (shows the loader). */
  omioBusyLegId?: string | null;
  /** Stop id whose Booking link is currently being fetched (shows the loader). */
  bookingBusyStopId?: string | null;
  /** Stop id that should open its city picker (the stop sheet's "Replace"
   *  hands the request over, since the picker lives on the row). */
  replaceRequestStopId?: string | null;
  /** Fired once the row has entered picker mode, so the page can clear it. */
  onReplaceRequestHandled?: () => void;
  /** The nights-per-stop suggestion is still being computed (backend
   *  nightsSuggestionService): the steppers show the dots loader meanwhile. */
  nightsPending?: boolean;
  /** Open "Search night trains nearby" for a leg: the action at the foot of
   *  the transport picker and the link on the card, on every leg. Absent on
   *  read-only views. */
  onSearchNightTrainsNearby?: (legId: string) => void;
  /** When set, the route head (trip-start date + nights wheel) becomes a sticky
   *  bar pinned this many px from the top — i.e. just below the page header. */
  stickyTop?: number;
  /** The stop or leg under the cursor (null when it leaves), so the page can
   *  bring it forward on the map. Pass only where hover is real (CAN_HOVER). */
  onHoverItem?: (item: TimelineHoverItem | null) => void;
  /** The stop picked on the map (desktop, JourneyRouteMap onStopClick): its
   *  card is outlined and scrolled into view. */
  selectedStopId?: string | null;
}

export interface TimelineHoverItem {
  kind: 'stop' | 'leg';
  id: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDate(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// Total trip nights = whole nights between departure and return (the wheel's
// denominator, per the prototype's renderDates). Null when the trip has no dates.
function tripNights(dep?: string | null, ret?: string | null): number | null {
  if (!dep || !ret) return null;
  const d = new Date(dep);
  const r = new Date(ret);
  if (Number.isNaN(d.getTime()) || Number.isNaN(r.getTime())) return null;
  const n = Math.round((r.getTime() - d.getTime()) / 86400000);
  return n >= 0 ? n : null;
}

// Transport modes Omio sells tickets for (train, bus, flight, ferry) — gates
// the per-leg "Search tickets in Omio" button. Mirrors the backend's
// omioAffiliateService.OMIO_MODES and the leg sheet's copy.
const OMIO_TIMELINE_MODES = new Set(['train', 'bus', 'airplane', 'boat']);

// Modes offered by the leg card's quick switcher. The shared list minus the
// "Let Jody decide" sentinel — a saved leg always has a concrete mode, and this
// picker sets one directly rather than asking the AI to choose.
const LEG_MODE_OPTIONS = TRANSPORT_MODE_OPTIONS.filter((o) => o.value !== 'auto');

// A displayed schedule date: label + whether it is out of order (a user-set
// leg date earlier than the route before it). Conflicts render in the app's
// danger red (#cf5a3c, same as High emissions) and never block the user.
interface ScheduleTag { label: string; conflict: boolean }

// Minimal calendar glyph for the date tags (gray; amber when conflicted).
const CalendarGlyph: React.FC<{ color: string }> = ({ color }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" aria-hidden style={{ flex: 'none' }}>
    <rect x="3" y="5" width="18" height="16" rx="2.5" />
    <path d="M3 10h18M8 2.5V6M16 2.5V6" />
  </svg>
);

const DateTag: React.FC<{ tag: ScheduleTag; light?: boolean }> = ({ tag, light }) => {
  // `light`: on the dark night-train card.
  const body = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: tag.conflict ? (light ? '#ffb4a1' : '#cf5a3c') : (light ? '#d5d6f0' : '#6c7872') }}>
      <CalendarGlyph color={tag.conflict ? (light ? '#ffb4a1' : '#cf5a3c') : (light ? '#b3b4dd' : '#aab4af')} />
      {tag.label}
    </span>
  );
  if (!tag.conflict) return body;
  // Conflicted dates are clickable: a real button (focusable), so tap/click
  // opens the shared Tooltip (hover/focus) with the fix hint.
  return (
    <Tooltip content="The trip dates are inconsistent. Please review and fix them." bare widthClassName="w-56">
      <button type="button" aria-label="Inconsistent date, tap for details" style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>
        {body}
      </button>
    </Tooltip>
  );
};

// ---- Expected calendar schedule (Paulo 2026-07-17) ---------------------------
// With a departure date set, the timeline shows WHEN the traveler is at each
// stop and the day each leg runs. The walk itself lives in
// utils.computeJourneySchedule (shared with the leg sheet); recomputed on every
// render so every nights-stepper tick updates all dates instantly.
function fmtDay(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}
// "16 Aug" (same day), "16–19 Aug" (same month), "28 Aug – 2 Sep" otherwise.
// Exported: the stop sheet shows the same window label next to its stepper.
export function fmtDayRange(fromMs: number, toMs: number): string {
  const a = new Date(fromMs);
  const b = new Date(toMs);
  if (a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth()) {
    if (a.getUTCDate() === b.getUTCDate()) return fmtDay(fromMs);
    return `${a.getUTCDate()}–${b.getUTCDate()} ${MONTHS[a.getUTCMonth()]}`;
  }
  return `${fmtDay(fromMs)} – ${fmtDay(toMs)}`;
}

// Keeps a loading state visible for at least `minMs` once it starts, so a value
// that lands quickly doesn't flash. `active` = the value is currently unknown.
export function useMinLoading(active: boolean, minMs = 3000): boolean {
  const [showing, setShowing] = useState(active);
  const startRef = useRef<number | null>(active ? Date.now() : null);
  useEffect(() => {
    if (active) {
      if (startRef.current == null) startRef.current = Date.now();
      setShowing(true);
      return;
    }
    const start = startRef.current;
    if (start == null) { setShowing(false); return; }
    const remaining = minMs - (Date.now() - start);
    if (remaining <= 0) { setShowing(false); startRef.current = null; return; }
    const t = setTimeout(() => { setShowing(false); startRef.current = null; }, remaining);
    return () => clearTimeout(t);
  }, [active, minMs]);
  return showing;
}

// Standard icon button: a 32px circular tap target (mobile-friendly). Tone sets
// the icon color. Keep ALL inline edit affordances on this component so they stay
// visually consistent — see docs/codebase/ui-conventions.md. Exported as the
// shared primitive (the stop picker's old ✓/✕ moved to StopPlacePicker).
export const IconBtn: React.FC<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'muted' | 'accent' | 'danger';
  children: React.ReactNode;
}> = ({ label, onClick, disabled, tone = 'muted', children }) => {
  const color = tone === 'danger' ? '#cf5a3c' : tone === 'accent' ? '#157a55' : '#6c7872';
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{ width: 32, height: 32, borderRadius: '50%', background: '#f4f6f3', border: 'none', padding: 0, lineHeight: 1, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1, color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {children}
    </button>
  );
};

// Line icons matching the prototype. Stroke inherits `currentColor` from the parent.
const PlusIcon: React.FC = () => (<svg width={15} height={15} viewBox="0 0 24 24" fill="none" aria-hidden><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" /></svg>);

// Animated "• • •" loader, reused for the leg price, the stop description and
// the compare-transport dialog's price cells (see docs/codebase/ui-conventions.md).
// The lp-blink keyframes live in this file's style block; consumers that render
// outside this tree (portaled dialogs) ship their own copy of the keyframes.
const dotStyle: React.CSSProperties = { width: 3, height: 3, borderRadius: '50%', background: '#6c7872', display: 'inline-block' };
export const Dots: React.FC = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }} aria-label="loading">
    <span className="lp-dot lp-d1" style={dotStyle} />
    <span className="lp-dot lp-d2" style={dotStyle} />
    <span className="lp-dot lp-d3" style={dotStyle} />
  </span>
);
// Stand-in for the nights stepper while the nights suggestion runs: the dots
// where the number lands, the "nights" word under it (same footprint).
export const NightsDots: React.FC = () => (
  <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: 36, padding: '6px 30px 3px', lineHeight: 1.05 }} aria-label="suggesting nights">
    <Dots />
    <span style={{ fontSize: 9.5, color: '#b3bbb6', marginTop: 5 }}>nights</span>
  </span>
);
const PriceDots: React.FC = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: '#6c7872' }} aria-label="estimating price">€<Dots /></span>
);

const Anchor: React.FC<{ label: string; date: string | null }> = ({ label, date }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
    <span style={{ width: 30, textAlign: 'center', color: '#157a55' }}>{label === 'Start' ? '▶' : '⏹'}</span>
    <span style={{ fontSize: 12, fontWeight: 700, color: '#6c7872', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
    {date && <span style={{ fontSize: 13, color: '#6c7872' }}>· {date}</span>}
  </div>
);

// Ring progress counter (prototype jody-journey-v2.html §route-head), used for
// both wheels: "x/y nights planned" (x = nights assigned across stops, y = total
// trip nights; amber when over-allocated) and "x/y transport booked" (x =
// booked legs, y = all legs). The verb differs per wheel: nights are an
// allocation ("planned"), tickets are a purchase ("booked", the 2026-08-24
// vocabulary change; identifiers stay `planned`).
const RING_R = 15.5;
const RING_C = 2 * Math.PI * RING_R; // circumference ≈ 97.4
const RingCounter: React.FC<{ x: number; y: number | null; word: string; verb: string; amberWhenOver?: boolean; note?: string | null }> = ({ x, y, word, verb, amberWhenOver, note }) => {
  const hasTotal = y != null && y > 0;
  const frac = hasTotal ? Math.max(0, Math.min(x / (y as number), 1)) : 0;
  const over = amberWhenOver && y != null && x > y;
  const color = over ? '#d9a23b' : '#157a55';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      {hasTotal && (
        <svg width={34} height={34} viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)', flex: 'none' }} aria-hidden>
          <circle cx="18" cy="18" r={RING_R} fill="none" stroke="#e4eae6" strokeWidth={3} />
          <circle cx="18" cy="18" r={RING_R} fill="none" stroke={color} strokeWidth={3} strokeLinecap="round" strokeDasharray={RING_C.toFixed(1)} strokeDashoffset={(RING_C * (1 - frac)).toFixed(1)} />
        </svg>
      )}
      <span style={{ fontSize: 12, fontWeight: 600, color: '#6c7872', lineHeight: 1.15 }}>
        <b style={{ color: '#16241d', fontWeight: 800 }}>{x}</b>{hasTotal ? `/${y}` : ''} {word}<br />{verb}
        {note && <><br /><span style={{ color: '#4f4e7c', whiteSpace: 'nowrap' }}>{note}</span></>}
      </span>
    </div>
  );
};

// The route header doubles as a sticky bar: given `stickyTop`, it pins just below
// the page header while the timeline scrolls, keeping the trip-start date + the
// nights-planned wheel in view. It folds in the old "Trip starts" anchor (left
// side), so there's no separate Start anchor. White background covers the stops
// that scroll underneath it.
const RouteHead: React.FC<{ nightsX: number; nightsY: number | null; nightsOnBoard: number; transportX: number; transportY: number; startDate: string | null; stickyTop?: number }> = ({ nightsX, nightsY, nightsOnBoard, transportX, transportY, startDate, stickyTop }) => (
  <div
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      background: '#fff', borderBottom: '1px solid rgba(20,40,30,0.06)',
      marginBottom: 12, paddingBottom: 10, paddingTop: stickyTop != null ? 6 : 0,
      ...(stickyTop != null ? { position: 'sticky' as const, top: stickyTop, zIndex: 10 } : {}),
    }}
  >
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span aria-hidden style={{ color: '#157a55', fontSize: 12, lineHeight: 1 }}>▶</span>
      <span style={{ fontSize: 13, color: '#6c7872', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {startDate
          ? <><b style={{ color: '#16241d', fontWeight: 700 }}>Trip starts</b> · {startDate}</>
          : <b style={{ color: '#16241d', fontWeight: 700 }}>Your route</b>}
      </span>
    </span>
    <span style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
      {/* Nights on a night train count as planned nights and are named
          (night trains spec §6.5: "shows in the nights wheel as on board"). */}
      <RingCounter x={nightsX} y={nightsY} word="nights" verb="planned" amberWhenOver note={nightsOnBoard > 0 ? `${nightsOnBoard} on board` : null} />
      {transportY > 0 && <RingCounter x={transportX} y={transportY} word="transport" verb="booked" />}
    </span>
  </div>
);

// One leg: a white card (emoji + route + stats) with a dashed rail. The stats
// keep our deterministic data (time/price-range/CO₂e). Tapping anywhere on the
// card opens the leg detail sheet (LegSheet) — the route text stays a real
// button for keyboard access; the pill still toggles Planned directly in place.
// A leg's title names its two places the way the stop cards do: the country's
// flag, then the city (utils.cityOnly leaves the country's name out).
// The tip about a direct night train is waved away per leg, and stays away
// on this device (a convenience: nothing depends on it, so storage that is
// blocked or full only means the tip comes back).
const NIGHT_HINT_KEY = 'jodyNightTrainTipsHidden';
function hiddenNightHints(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(NIGHT_HINT_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
const nightHintDismissed = (legId: string): boolean => hiddenNightHints().includes(legId);
function dismissNightHint(legId: string): void {
  try {
    localStorage.setItem(NIGHT_HINT_KEY, JSON.stringify([...hiddenNightHints().filter((id) => id !== legId), legId].slice(-200)));
  } catch {
    /* the tip comes back next time */
  }
}

const Leg: React.FC<{ leg: TimelineLeg; from: string; to: string; fromFlag?: string; toFlag?: string; dateLabel?: ScheduleTag | null; busy?: boolean; onOpen?: (legId: string, source?: 'card' | 'pill') => void; onTogglePlanned?: (legId: string, planned: boolean) => void; onOmio?: (legId: string) => void; omioBusy?: boolean; onChangeMode?: (legId: string, mode: string) => void; onNightTrainBooking?: (legId: string) => void; onSearchNearby?: (legId: string) => void }> = ({ leg, from, to, fromFlag, toFlag, dateLabel, busy, onOpen, onTogglePlanned, onOmio, omioBusy, onChangeMode, onNightTrainBooking, onSearchNearby }) => {
  // Night train: the dark "night sky" card (night trains spec §7.1, prototype
  // .legpill.night). Everything it says comes from the timetable data.
  const night = isNightTrainMode(leg.transportMode) && leg.nightTrain ? leg.nightTrain : null;
  // A user-entered value for the leg's current mode replaces the estimate
  // (user-editable costs & times; entered in the leg sheet / compare dialog).
  const ovr = currentModeOverride(leg);
  const durMin = coerceDecimal(leg.durationMinutes, NaN);
  const hrs = coerceDecimal(leg.durationHours, NaN);
  const estMin = Number.isFinite(durMin) ? durMin : (Number.isFinite(hrs) ? hrs * 60 : NaN);
  const resolvedMin = ovr?.durationMinutes != null ? Number(ovr.durationMinutes) : estMin;
  // Estimates are rounded up; a user's own time and a night train's timetable
  // ride are exact.
  const exactTime = ovr?.durationMinutes != null || (isNightTrainMode(leg.transportMode) && leg.nightTrain != null);
  const timeStr = !Number.isFinite(resolvedMin) ? '' : (exactTime ? formatDurationExact(resolvedMin) : formatDurationMin(resolvedMin));
  const lowC = coerceDecimal(leg.priceBandLowCents, NaN);
  const highC = coerceDecimal(leg.priceBandHighCents, NaN);
  const priced = ovr?.costCents != null || (Number.isFinite(lowC) && Number.isFinite(highC));
  const lowEur = Math.round(lowC / 100);
  const highEur = Math.round(highC / 100);
  const priceStr = ovr?.costCents != null
    ? formatEuroExact(Number(ovr.costCents))
    : (priced ? (lowEur === highEur ? `€${lowEur}` : `€${lowEur}–${highEur}`) : '€…');
  const showPriceLoader = useMinLoading(!priced);

  // Prototype leg stats: time · price · "{Low/Medium/High} emissions" (colored
  // level word only, like the prototype rows; the exact kg lives in the leg
  // detail sheet's Details section, in parentheses). An unknown/auto mode
  // shows NO emissions token (a defaulted "Low" would be misleading).
  const emiLevel = getEmissionLevel(leg.transportMode);
  const emiColor = EMISSION_LEVEL_COLOR[emiLevel];
  const emiLabel = EMISSION_LEVEL_LABEL[emiLevel];
  const tokens: React.ReactNode[] = [];
  if (Number.isFinite(resolvedMin)) {
    // Flight time estimates carry the ground overhead (2 h 30); say so right where
    // the number appears (same ⓘ + copy as the leg sheet's Details row) so a
    // "5:20 to Lisbon" against a 3:30 airline schedule reads as airport time,
    // not an error (Paulo, 2026-08-15). The WHOLE time is the trigger, with
    // the shared Tooltip's dotted-underline affordance (Paulo's follow-up:
    // hovering the hours must work too, not just the ⓘ). Flights only;
    // user-entered times are theirs — no tooltip.
    tokens.push(
      isAirplaneMode(leg.transportMode) && ovr?.durationMinutes == null ? (
        <Tooltip content={FLIGHT_TIME_TOOLTIP} widthClassName="w-64">
          <span>
            {timeStr}
            <span aria-hidden style={{ marginLeft: 3, color: '#aab4af', fontWeight: 700 }}>ⓘ</span>
          </span>
        </Tooltip>
      ) : timeStr
    );
  }
  tokens.push(showPriceLoader ? <PriceDots key="price" /> : priceStr);
  if (isKnownTransportMode(leg.transportMode)) {
    tokens.push(
      <span key="emi">
        {/* "Very low" says why (a night train saves a hotel night too); on
            the dark night card the label takes the map's neon green. */}
        {emiLevel === 'vlow' && isNightTrainMode(leg.transportMode) ? (
          <Tooltip content={NIGHT_TRAIN_EMISSIONS_NOTE} widthClassName="w-56" bare>
            <span style={{ color: night ? '#5cf0a3' : emiColor, fontWeight: 600, cursor: 'help' }}>{emiLabel}</span>
          </Tooltip>
        ) : (
          <span style={{ color: night ? '#86d7b1' : emiColor, fontWeight: 600 }}>{emiLabel}</span>
        )} emissions
      </span>
    );
  }
  // Night trains on the card (Paulo 2026-09-29: the feature was hidden in
  // the transport list). Only where the two places are far enough apart for
  // a night train, and not once the ticket is booked.
  const searchable = Boolean(onSearchNearby) && !leg.planned && canSearchNearby(leg.nightTrainStatus);
  // The search would find nothing here (the backend looked when it read the
  // journey): the card says so in place of the link, and nothing opens.
  const searchBlocked = searchable ? searchBlockedReason(leg.nightTrainSearch, Boolean(night)) : null;
  // A direct night train the traveller is not taking: said on the card,
  // with a way to take it, until they take it or wave it away.
  const [hintDismissed, setHintDismissed] = useState(() => nightHintDismissed(leg.id));
  const showHint = searchable && !night && Boolean(onChangeMode) && leg.nightTrainStatus === 'available' && !hintDismissed;
  // The link in the stats line; the hint carries its own while it shows.
  const nightSearch = searchable && !showHint ? (
    <NightTrainSearchButton
      key="night-search"
      variant={night ? 'link-night' : 'link'}
      label={night ? NEARBY_SEARCH_OTHER_LABEL : NEARBY_SEARCH_LABEL}
      ariaLabel={`${night ? NEARBY_SEARCH_OTHER_LABEL : NEARBY_SEARCH_LABEL}: ${from} to ${to}`}
      disabled={busy}
      blocked={searchBlocked}
      onClick={() => onSearchNearby!(leg.id)}
    />
  ) : null;
  // Night card: the times are the information, so the duration rides with
  // them; the stats line keeps the price (an estimate, marked) and emissions.
  const onBoard = legNightsOnBoard(leg);
  const nightNote = night ? cardRunsNote(night) : null;
  const nightTokens: React.ReactNode[] = night
    ? [
      showPriceLoader ? <PriceDots key="price" /> : <span key="price">{priceStr}{ovr?.costCents == null && priced ? '*' : ''}</span>,
      ...tokens.slice(-1),
      <TrustMark key="trust" train={night} light />,
      ...(nightSearch ? [nightSearch] : []),
    ]
    : [];
  if (!night && nightSearch) tokens.push(nightSearch);

  const route = (
    <>
      {fromFlag && <span aria-hidden>{fromFlag} </span>}{from} → {toFlag && <span aria-hidden>{toFlag} </span>}{to}
    </>
  );

  return (
    <div className="rt-row legrow">
      <div className="rt-gutter" />
      <div
        className={night ? 'rt-legpill night' : (showHint ? 'rt-legpill has-hint' : 'rt-legpill')}
        onClick={onOpen && !busy ? () => onOpen(leg.id) : undefined}
        style={onOpen && !busy ? { cursor: 'pointer' } : undefined}
      >
        {/* The mode emoji doubles as the quick transport switcher: a caret next
            to it advertises that it opens a picker (users previously had to
            discover mode-changing by poking — interview finding). The list only
            appears on click, so the card itself stays as dense as it was. */}
        {onChangeMode ? (
          <span className="rt-lemoji" onClick={(e) => e.stopPropagation()}>
            <Dropdown
              variant="bare"
              portal
              hideCheck={false}
              ariaLabel={`Transport for ${from} to ${to}: ${getModeLabel(leg.transportMode)}. Change it`}
              // "Night train" says where the leg stands: a direct one to
              // pick, or none and the search in its place. Where there is a
              // night train to pick or in use, the search is at the foot.
              options={modeOptionsForLeg(
                LEG_MODE_OPTIONS,
                { status: leg.nightTrainStatus, note: leg.nightTrainNote, available: leg.nightTrainAvailable, current: isNightTrainMode(leg.transportMode), search: leg.nightTrainSearch },
                onSearchNearby ? () => onSearchNearby(leg.id) : undefined,
              )}
              footer={onSearchNearby && !searchBlockedReason(leg.nightTrainSearch, Boolean(night)) && (night || leg.nightTrainStatus === 'available')
                ? { label: <><span aria-hidden>🌙 </span>{night ? NEARBY_SEARCH_OTHER_LABEL : NEARBY_SEARCH_LABEL}</>, onSelect: () => onSearchNearby(leg.id) }
                : undefined}
              value={String(leg.transportMode || '')}
              onChange={(m) => { if (m !== leg.transportMode) onChangeMode(leg.id, m); }}
              disabled={busy}
              triggerLabel={<span title={getModeLabel(leg.transportMode)}>{getTransportEmoji(leg.transportMode)}</span>}
            />
          </span>
        ) : (
          <span className="rt-lemoji" title={getModeLabel(leg.transportMode)}>{getTransportEmoji(leg.transportMode)}</span>
        )}
        <div className="rt-linfo">
          <div className="rt-lroute">
            {onOpen ? (
              <button type="button" className="rt-legbtn" disabled={busy} onClick={(e) => { e.stopPropagation(); onOpen(leg.id); }} aria-label={`Travel details: ${from} to ${to}`}>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{route}</span>
                <span aria-hidden className="rt-legchev">›</span>
              </button>
            ) : (
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{route}</span>
            )}
          </div>
          {night ? (
            <>
              {/* Three rows (Paulo 2026-09-28): the chips; the train's own
                  details on the left with the booking button on the right of
                  the same row; then one row of stats. Two blocks, so the
                  phone layout can give the first the card's full width and
                  keep the stats beside the mode picker. */}
              <div className="rt-nbody">
                <div className="rt-nline">
                  <span className="rt-nchip">🌙 {nightTrainChip(night)}</span>
                  <span className="rt-nchip">🛏️ {replacesLabel(onBoard)}</span>
                </div>
                <div className="rt-ndetail">
                  <div className="rt-ninfo">
                    {/* Wraps between the times and the ride, never inside the times. */}
                    <div className="rt-ntimes"><span style={{ whiteSpace: 'nowrap' }}>{timesOnly(night)}</span> {rideNote(night)}</div>
                    <div className="rt-nprod">{productLine(night)}</div>
                    {nightNote && <div className="rt-nnote" style={nightNote.tone === 'warn' ? { color: '#ffd58a' } : undefined}>{nightNote.text}</div>}
                  </div>
                  {/* Book with the operator, or on Omio where the operator
                      is sold there (spec §7.1); the commission note only
                      applies to Omio. */}
                  {onNightTrainBooking && !leg.planned && (
                    <span className="rt-ncta" onClick={(e) => e.stopPropagation()}>
                      <Tooltip
                        content={night.preferredChannel === 'omio'
                          ? 'Jody may earn a commission when you book through this link. This never changes your price.'
                          : `Opens ${night.operatorName || night.productName}, where this train is sold. Jody earns nothing from this link.`}
                        bare
                      >
                        <button
                          type="button"
                          data-share-ok
                          onClick={() => onNightTrainBooking(leg.id)}
                          disabled={busy || omioBusy}
                          className="rt-nbook"
                          style={{ cursor: omioBusy ? 'default' : 'pointer', opacity: omioBusy ? 0.7 : 1 }}
                        >
                          {omioBusy ? <span style={{ whiteSpace: 'nowrap' }}>Opening <Dots /></span> : bookingLabel(night, 'card')}
                        </button>
                      </Tooltip>
                    </span>
                  )}
                </div>
              </div>
              {/* One row: "€70–180* · Low emissions · Timetable checked".
                  Each separator belongs to the item after it and sits in
                  that item's left padding, which the row clips at the start
                  of a line: where a narrow screen wraps the row, no
                  separator is left hanging. */}
              <div className="rt-nstats">
                <div className="rt-nstats-in">
                  {nightTokens.map((t, i) => <span key={i} className="rt-nstat">{t}</span>)}
                </div>
              </div>
            </>
          ) : (
          <div className="rt-lstats">
            {tokens.map((t, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ color: '#b3bbb6' }}>·</span>}
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>{t}</span>
              </React.Fragment>
            ))}
          </div>
          )}
        </div>
        {/* Pill + travel day stacked, centered (mirrors the stop card's
            stepper + dates column — Paulo 2026-07-18). A SIBLING of the text
            block so the date doesn't push the stats away from the title.
            The pill here is a status DISPLAY: tapping it opens the leg sheet
            (where Planned is edited) instead of toggling in place (Paulo
            2026-07-18). stopPropagation so a conflict-tooltip tap doesn't also
            bubble to the card. */}
        <span className="rt-lside" onClick={(e) => e.stopPropagation()}>
          {/* Tag only when the ticket IS planned (Paulo 2026-08-18, matching
              the stop card): Unplanned lives in the leg sheet, where it's
              edited. Tapping the tag still opens the sheet. */}
          {onTogglePlanned && leg.planned && (
            <span className="rt-lpill" style={{ display: 'inline-flex' }}>
              <PlannedPill planned disabled={busy} onPress={() => onOpen?.(leg.id, 'pill')} />
            </span>
          )}
          {dateLabel && <span className="rt-ldate" style={{ display: 'inline-flex' }}><DateTag tag={dateLabel} light={!!night} /></span>}
          {/* Direct affiliate CTA on the card (shortest path to a booking
              click, Paulo 2026-07-23): unplanned legs, Omio-covered modes.
              The Tooltip carries the commission disclosure. */}
          {onOmio && !leg.planned && OMIO_TIMELINE_MODES.has(String(leg.transportMode || '')) && (
            <span className="rt-lcta" style={{ display: 'inline-flex', marginTop: 6 }}>
            <Tooltip content="Jody may earn a commission when you book through this link. This never changes your price." bare>
              <button
                type="button"
                data-share-ok
                onClick={() => onOmio(leg.id)}
                disabled={busy || omioBusy}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none',
                  font: 'inherit', fontSize: 11, fontWeight: 700, color: '#fff',
                  background: '#157a55', border: 'none', borderRadius: 999, padding: '6px 10px',
                  cursor: omioBusy ? 'default' : 'pointer', opacity: omioBusy ? 0.7 : 1, whiteSpace: 'nowrap',
                }}
              >
                {omioBusy ? <>Opening <Dots /></> : 'Search tickets'}
              </button>
            </Tooltip>
            </span>
          )}
        </span>
        {/* A balloon that stays in the card: its tip points at the transport
            picker, where the night train is taken. In the card and not
            floating over it, so it never covers a neighbour and scrolls
            with the page. */}
        {showHint && (
          <div className="rt-nhint" onClick={(e) => e.stopPropagation()}>
            <span aria-hidden style={{ fontSize: 15, lineHeight: 1, flex: 'none' }}>🌙</span>
            <span style={{ flex: '1 1 150px', minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 700 }}>A direct night train runs here</span>
              <span style={{ display: 'block', color: '#5a5b86' }}>Sleep on board and wake up in {to}.</span>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flex: 'none', flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={busy}
                onClick={() => onChangeMode!(leg.id, 'night_train')}
                style={{ font: 'inherit', fontSize: 11.5, fontWeight: 700, color: '#fff', background: '#3b3c64', border: 'none', borderRadius: 999, padding: '6px 11px', cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
              >
                Take the night train
              </button>
              {!searchBlocked && (
                <NightTrainSearchButton variant="link" label="See it" ariaLabel={`${NEARBY_SEARCH_LABEL}: ${from} to ${to}`} disabled={busy} onClick={() => onSearchNearby!(leg.id)} />
              )}
              <button
                type="button"
                aria-label="Hide this tip"
                onClick={() => { dismissNightHint(leg.id); setHintDismissed(true); }}
                style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: '#8584a6', display: 'inline-flex' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// One stop: a numbered rail + a white content card (name, description, things-to-do).
const Stop: React.FC<{
  stop: TimelineStop;
  /** Start, End or the stop's number (stopSequenceLabels): display only. */
  seq: StopSequenceLabel;
  /** Expected "16–19 Aug" window at this stop (null without any date anchor). */
  dates?: ScheduleTag | null;
  busy?: boolean;
  onRemove?: (stopId: string) => void;
  onReplace?: (stopId: string, city: CityResult, suggestion?: StopSuggestionPick) => void;
  onSuggestPlaces?: SuggestPlacesFn;
  onPreviewPlace?: PreviewPlaceFn;
  onNightsChange?: (stopId: string, nights: number) => void;
  onNightsSaved?: (stopId: string) => void;
  onTogglePlanned?: (stopId: string, planned: boolean) => void;
  onOpen?: (stopId: string, source?: 'card' | 'pill') => void;
  onBooking?: (stopId: string) => void;
  bookingBusy?: boolean;
  /** The stop sheet asked for this row's city picker (see RouteTimelineProps). */
  replaceRequested?: boolean;
  onReplaceRequestHandled?: () => void;
  nightsPending?: boolean;
  /** Picked on the map: outlined (see RouteTimelineProps.selectedStopId). */
  selected?: boolean;
}> = ({ stop, seq, dates, busy, onRemove, onReplace, onSuggestPlaces, onPreviewPlace, onNightsChange, onNightsSaved, onTogglePlanned, onOpen, onBooking, bookingBusy, replaceRequested, onReplaceRequestHandled, nightsPending, selected }) => {
  const [editing, setEditing] = useState(false);
  // Honour a Replace requested from the stop sheet: open the picker here, then
  // tell the page so the one-shot request doesn't re-fire.
  useEffect(() => {
    if (!replaceRequested) return;
    setEditing(true);
    onReplaceRequestHandled?.();
  }, [replaceRequested, onReplaceRequestHandled]);
  // 2-line clamp with a "More" affordance when the text overflows; More (and
  // any card click) opens the stop sheet, which shows the full description.
  const [descClamped, setDescClamped] = useState(false);
  const descRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    const check = () => setDescClamped(el.scrollHeight > el.clientHeight + 1);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [stop.description]);
  const city = stop.city || stop.location || '';
  const flag = getCountryFlag(stop.country);
  const editable = Boolean(onReplace || onRemove);
  const showDescLoader = useMinLoading(!stop.description && !!city && city !== 'Unknown');
  const showNightsLoader = useMinLoading(Boolean(nightsPending));
  const stayCostCents = stop.stayDetails?.costCents;

  // The page confirms the replace (ConfirmationDialog), so a pick goes straight to it.
  const pickReplacement = (c: CityResult, suggestion?: StopSuggestionPick) => { if (onReplace && stop.id) onReplace(stop.id, c, suggestion); setEditing(false); };

  return (
    <div className="rt-row stoprow" data-stop-id={stop.id || undefined}>
      <div className="rt-gutter">
        <div className={seq.kind === 'stop' ? 'rt-num' : 'rt-num is-word'}>
          {seq.kind === 'stop' ? seq.number : seq.kind === 'start' ? 'Start' : 'End'}
        </div>
      </div>
      {/* The WHOLE card opens the stop sheet (Paulo 2026-08-18) — except the
          right column (stepper/pill/CTA, which stopPropagation below), the
          hover edit icons, and the replace-editing state. The name stays a
          real button for keyboard access. */}
      <div
        className={selected ? 'rt-card is-selected' : 'rt-card'}
        onClick={onOpen && stop.id && !editing && !busy ? () => onOpen(stop.id!, 'card') : undefined}
        style={onOpen && stop.id && !editing && !busy ? { cursor: 'pointer' } : undefined}
      >
        {editing && stop.id ? (
          <StopPlacePicker
            target={{ action: 'replace', stopId: stop.id }}
            title={<>Replace <span style={{ fontWeight: 700, color: '#16241d' }}>{flag ? `${flag} ` : ''}{city}</span></>}
            replacingCity={city}
            busy={busy}
            onSuggest={onSuggestPlaces}
            onPreview={onPreviewPlace}
            onPick={pickReplacement}
            onCancel={() => setEditing(false)}
          />
        ) : (
          // Prototype .stoptop: a 2×2 grid — row 1: name (+ hover actions) | status
          // pill; row 2: description | nights stepper. Pill and stepper right-align
          // (justify-self end); the stepper hugs the top of its row like the
          // prototype (align-self start, slight negative right margin).
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 10, rowGap: 4, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, minHeight: 30 }}>
              {/* Prototype .stopbtn: the stop name opens the stop detail sheet
                  (stay details + Booking CTA) when the page provides onOpen. */}
              {onOpen && stop.id ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onOpen(stop.id!, 'card')}
                  aria-label={`Stay details: ${city}`}
                  style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, color: '#16241d', fontSize: 16, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {flag ? `${flag} ` : ''}{city}
                  {/* Same quiet chevron the leg row uses, so "this opens a
                      detail sheet" reads identically on both card types. */}
                  <span aria-hidden className="rt-legchev">›</span>
                </button>
              ) : (
                <span style={{ fontWeight: 700, color: '#16241d', fontSize: 16, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{flag ? `${flag} ` : ''}{city}</span>
              )}
              {editable && stop.id && (
                <span className="stop-actions" onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', flexShrink: 0 }}>
                  <KebabMenu
                    ariaLabel={`Options for ${city}`}
                    disabled={busy}
                    items={[
                      { label: 'Details', icon: KebabIcons.open, hidden: !onOpen, onSelect: () => onOpen?.(stop.id!, 'card') },
                      { label: 'Replace', icon: KebabIcons.swap, hidden: !onReplace, onSelect: () => setEditing(true) },
                      { label: 'Delete', icon: KebabIcons.trash, tone: 'danger', hidden: !onRemove, onSelect: () => onRemove?.(stop.id!) },
                    ]}
                  />
                </span>
              )}
            </div>
            {/* The tag renders only once the stay IS planned (Paulo
                2026-08-18) — the Unplanned state lives in the stop sheet,
                where it's edited. Tapping the tag still opens the sheet.
                When unplanned this r1c2 cell is omitted entirely so the
                stepper column below can climb into it. */}
            {stop.id && stop.planned && (onOpen || onTogglePlanned) && (
              <span style={{ justifySelf: 'end' }} onClick={(e) => e.stopPropagation()}>
                <PlannedPill planned disabled={busy} onPress={() => (onOpen ? onOpen(stop.id!, 'pill') : onTogglePlanned!(stop.id!, false))} />
              </span>
            )}
            {/* Two clamped lines; when the text overflows, a "More" link sits
                at the end of line 2 and opens the stop sheet (Paulo
                2026-08-18 — no in-place expansion; the sheet shows it all).
                Explicit column 1: with the pill cell absent on unplanned
                stays, auto-placement would otherwise float this to r1c2. */}
            <div style={{ fontSize: 13, lineHeight: 1.45, color: '#6c7872', minWidth: 0, gridColumn: 1 }}>
              {stop.description ? (
                <div style={{ position: 'relative' }}>
                  <div ref={descRef} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }}>
                    {stop.description}
                  </div>
                  {descClamped && onOpen && stop.id && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpen(stop.id!, 'card'); }}
                      style={{
                        position: 'absolute', right: 0, bottom: 0,
                        background: 'linear-gradient(to right, rgba(255,255,255,0), #fff 35%)',
                        border: 'none', padding: '0 0 0 30px', cursor: 'pointer',
                        font: 'inherit', fontSize: 13, lineHeight: 1.45,
                      }}
                    >
                      <span style={{ color: '#6c7872' }}>… </span>
                      <span style={{ color: '#157a55', fontWeight: 600 }}>More</span>
                    </button>
                  )}
                </div>
              ) : showDescLoader ? <Dots /> : null}
              {/* Stay price, once entered in the sheet — same stats voice as
                  the leg card's tokens (user value, so exact euros). Labelled
                  "Stay": the bed emoji alone did not say what the number was
                  (Paulo 2026-08-28), and "Stay" is the vocabulary the sheet and
                  the Booking CTA already use ("Stay booked", "Where you'll
                  stay"), shorter than "Accommodation" for this dense row and
                  easier for non-native speakers. A zero cost reads "Free" in
                  the brand green rather than "€0.00", which looks like a
                  missing value. */}
              {stayCostCents != null && (
                <div className="rt-lstats" style={{ marginTop: 5 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span aria-hidden>🛏️</span>
                    <span>Stay ·</span>
                    {Number(stayCostCents) === 0
                      ? <span style={{ color: '#157a55', fontWeight: 700 }}>Free</span>
                      : <span>{formatEuroExact(Number(stayCostCents))}</span>}
                  </span>
                </div>
              )}
            </div>
            {/* Stepper + expected dates stacked (both are "when am I here" —
                Paulo 2026-07-18); the date centers under the stepper. The
                whole column swallows clicks: the stepper is the card-click
                exception (Paulo 2026-08-18), and the sheet has its own copy
                of all three. Stepper keyed on the stored hours so a nights
                change made in the SHEET remounts this instance in sync
                (card-only stepping never patches the journey, so no remount
                mid-burst). */}
            {/* Unplanned: no pill above, so the column climbs into row 1 and
                the card stays short (Paulo 2026-08-18). Planned: the tag
                holds r1c2 and the column sits below it, as before. */}
            <span onClick={(e) => e.stopPropagation()} style={{ justifySelf: 'end', alignSelf: 'start', marginRight: -3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, gridColumn: 2, gridRow: stop.planned ? undefined : '1 / span 2' }}>
              {stop.id && (showNightsLoader
                ? <NightsDots />
                : <NightsStepper stopId={stop.id} initialHours={stop.stopDurationHours ?? null} onNightsChange={(n) => onNightsChange?.(stop.id!, n)} onSaved={() => onNightsSaved?.(stop.id!)} />)}
              {dates && <DateTag tag={dates} />}
              {/* Direct Booking CTA on the card, bottom-right under the dates
                  (Paulo 2026-08-18) — the stop twin of the leg card's "Search
                  tickets". Unplanned stays only; no commission tooltip while
                  the link is plain (docs/integrations/booking.md). */}
              {onBooking && stop.id && !stop.planned && (
                <button
                  type="button"
                  data-share-ok
                  onClick={() => onBooking(stop.id!)}
                  disabled={busy || bookingBusy}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none', marginTop: 6,
                    font: 'inherit', fontSize: 11, fontWeight: 700, color: '#fff',
                    background: '#157a55', border: 'none', borderRadius: 999, padding: '6px 10px',
                    cursor: bookingBusy ? 'default' : 'pointer', opacity: bookingBusy ? 0.7 : 1, whiteSpace: 'nowrap',
                  }}
                >
                  {bookingBusy ? <>Opening <Dots /></> : 'Search stays'}
                </button>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// The "+" add-here on the rail between stops (prototype `.addhere`). afterStopId =
// the stop the new one follows (null = prepend). Opens the inline place picker.
const AddHere: React.FC<{ afterStopId: string | null; busy?: boolean; onAdd: (afterStopId: string | null, city: CityResult, suggestion?: StopSuggestionPick) => void; onSuggest?: SuggestPlacesFn; onPreview?: PreviewPlaceFn }> = ({ afterStopId, busy, onAdd, onSuggest, onPreview }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rt-row">
      <div className="rt-gutter rt-gutter-center">
        {!open && (
          <button type="button" className="rt-addhere" title="Add a stop" aria-label="Add a stop" disabled={busy} onClick={() => setOpen(true)}>
            <PlusIcon />
          </button>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {open && (
          <div className="rt-card">
            <StopPlacePicker
              target={{ action: 'add', afterStopId }}
              title="Add a stop here"
              busy={busy}
              onSuggest={onSuggest}
              onPreview={onPreview}
              onPick={(c, suggestion) => { onAdd(afterStopId, c, suggestion); setOpen(false); }}
              onCancel={() => setOpen(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const RAIL_CSS = `
  /* A single continuous dashed "spine" runs down the rail column; the numbered
     circles and the "+" sit on top of it (opaque), so the line reads as one
     harmonious dashed line interrupted only by them. */
  .rt-track { position: relative; }
  .rt-spine { position: absolute; left: 15px; top: 4px; bottom: 4px; width: 2px; margin-left: -1px; z-index: 0; background: repeating-linear-gradient(to bottom, #cdd5d0, #cdd5d0 3px, transparent 3px, transparent 7px); }
  .rt-row { display: flex; gap: 12px; position: relative; z-index: 1; }
  .rt-gutter { width: 30px; flex: none; display: flex; flex-direction: column; align-items: center; }
  .rt-gutter-center { justify-content: center; }
  .rt-num { width: 30px; height: 30px; border-radius: 50%; background: #e7f1ec; color: #0e5c40; display: grid; place-items: center; font-weight: 700; font-size: 13px; flex: none; margin-top: 13px; }
  .rt-num.is-word { font-size: 10px; letter-spacing: -0.1px; }
  .rt-card { flex: 1; min-width: 0; border: 1px solid rgba(20,40,30,0.08); border-radius: 14px; background: #fff; padding: 11px 13px; position: relative; margin: 4px 0; }
  .rt-card, .rt-legpill { transition: border-color 150ms ease, box-shadow 150ms ease; }
  /* Focus outline, one look for both ways in: a stop picked on the map
     (.is-selected) and a stop or leg hovered while the map follows the
     timeline (.rt-hover-focus, set only where hover is real). */
  .rt-card.is-selected { border-color: #157a55; box-shadow: 0 0 0 1px #157a55, 0 2px 10px rgba(21,122,85,0.14); }
  @media (hover: hover) {
    .rt-hover-focus .stoprow:hover .rt-card,
    .rt-hover-focus .legrow:hover .rt-legpill { border-color: #157a55; box-shadow: 0 0 0 1px #157a55, 0 2px 10px rgba(21,122,85,0.14); }
  }
  .rt-card::before { content: ''; position: absolute; left: -12px; top: 20px; width: 11px; height: 2px; background: #cdd5d0; }
  .rt-legpill { flex: 1; min-width: 0; display: flex; align-items: flex-start; gap: 10px; padding: 10px 13px; background: #fff; border: 1px solid rgba(20,40,30,0.08); border-radius: 13px; margin: 6px 0; }
  .rt-lemoji { font-size: 18px; line-height: 1.2; align-self: center; flex: none; }
  .rt-linfo { flex: 1; min-width: 0; }
  .rt-lroute { display: flex; align-items: center; gap: 7px; font-weight: 600; font-size: 13px; color: #16241d; min-height: 32px; }
  /* Pill + date + Omio CTA column at the card's right (desktop). Styled here,
     NOT inline, so the mobile grid below can restyle it — an inline display
     would beat the media query. */
  .rt-lside { display: inline-flex; flex-direction: column; align-items: center; gap: 3px; flex: none; }
  /* The tip about a direct night train: a balloon inside the card, a row of
     its own under everything else, its tip pointing up at the transport
     picker. */
  .rt-legpill.has-hint { flex-wrap: wrap; }
  .rt-nhint { position: relative; flex: 1 0 100%; display: flex; align-items: center; flex-wrap: wrap; gap: 8px 10px; margin-top: 8px; padding: 9px 11px; border-radius: 11px; background: #ecebf7; color: #34335c; font-size: 12px; line-height: 1.35; cursor: default; }
  .rt-nhint::before { content: ""; position: absolute; top: -5px; left: 9px; width: 10px; height: 10px; background: #ecebf7; transform: rotate(45deg); border-radius: 2px; }
  /* Night train: a distinct "night sky" card (prototype .legpill.night,
     docs/product/night-trains/spec/prototype/jody-journey-v2_7.html). */
  /* The stars are part of the background (one small radial gradient each,
     placed in % so they spread with the card's size), so text and buttons
     always paint over them. They sit along the edges and in the gaps the
     text leaves at both widths: a star beside a line of text reads as
     punctuation. */
  .rt-legpill.night {
    border-color: #3b3c64; color: #e8e9f7;
    background:
      radial-gradient(circle at 20% 4%, rgba(255,255,255,0.3) 0, rgba(255,255,255,0.3) 0.8px, transparent 1.4px),
      radial-gradient(circle at 55% 3%, rgba(255,255,255,0.4) 0, rgba(255,255,255,0.4) 0.9px, transparent 1.5px),
      radial-gradient(circle at 82% 4%, rgba(255,255,255,0.5) 0, rgba(255,255,255,0.5) 1.1px, transparent 1.7px),
      radial-gradient(circle at 70% 24%, rgba(255,255,255,0.4) 0, rgba(255,255,255,0.4) 0.9px, transparent 1.5px),
      radial-gradient(circle at 96% 40%, rgba(255,255,255,0.75) 0, rgba(255,255,255,0.75) 1.2px, transparent 1.8px),
      radial-gradient(circle at 88% 56%, rgba(255,255,255,0.5) 0, rgba(255,255,255,0.5) 1.0px, transparent 1.6px),
      radial-gradient(circle at 97% 68%, rgba(255,255,255,0.45) 0, rgba(255,255,255,0.45) 0.9px, transparent 1.5px),
      radial-gradient(circle at 60% 97%, rgba(255,255,255,0.35) 0, rgba(255,255,255,0.35) 0.8px, transparent 1.4px),
      radial-gradient(circle at 33% 96%, rgba(255,255,255,0.3) 0, rgba(255,255,255,0.3) 0.8px, transparent 1.4px),
      radial-gradient(circle at 1.5% 28%, rgba(255,255,255,0.3) 0, rgba(255,255,255,0.3) 0.8px, transparent 1.4px),
      radial-gradient(circle at 2% 72%, rgba(255,255,255,0.35) 0, rgba(255,255,255,0.35) 0.8px, transparent 1.4px),
      radial-gradient(circle at 90% 97%, rgba(255,255,255,0.4) 0, rgba(255,255,255,0.4) 0.9px, transparent 1.5px),
      linear-gradient(125deg, #3b3c64, #4f4e7c);
    /* A grid at every width: the train's block and the stats row run under
       the date, across the card's full width. */
    display: grid; grid-template-columns: auto minmax(0, 1fr) auto; grid-template-areas: 'emoji title side' 'emoji nbody nbody' 'emoji nstats nstats'; column-gap: 10px; row-gap: 4px; align-items: center;
  }
  .rt-legpill.night .rt-linfo { display: contents; }
  .rt-legpill.night .rt-lroute { grid-area: title; min-width: 0; }
  .rt-legpill.night .rt-lemoji { grid-area: emoji; }
  .rt-legpill.night .rt-lside { grid-area: side; align-self: center; }
  .rt-nbody { grid-area: nbody; }
  /* The train's details at the left, the booking button at the right of the
     same row, at every width. A long operator name ("Book at European
     Sleeper") wraps inside the button, on two balanced lines, and the
     details wrap beside it, so neither pushes the other out. */
  .rt-ndetail { display: flex; align-items: center; gap: 12px; width: 100%; min-width: 0; }
  .rt-ninfo { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: flex-start; gap: 4px; }
  .rt-ncta { display: inline-flex; flex: none; max-width: 128px; margin-left: auto; }
  .rt-nbook { font: inherit; font-size: 11px; font-weight: 700; line-height: 1.25; color: #2f305a; background: #fff; border: none; border-radius: 15px; padding: 6px 11px; text-align: center; white-space: normal; text-wrap: balance; }
  @media (max-width: 480px) { .rt-ncta { max-width: 112px; } }
  .rt-nstats { grid-area: nstats; min-width: 0; overflow: hidden; color: #b7b9e2; font-size: 12.5px; margin-top: 2px; }
  .rt-nstats-in { display: flex; align-items: center; flex-wrap: wrap; row-gap: 3px; margin-left: -17px; }
  .rt-nstat { position: relative; display: inline-flex; align-items: center; padding-left: 17px; white-space: nowrap; }
  .rt-nstat::before { content: '·'; position: absolute; left: 6px; color: #8a8bb8; }
  .rt-legpill.night .rt-legbtn { color: #fff; }
  .rt-legpill.night .rt-legchev { color: #b3b4dd; }
  .rt-legpill.night .rt-lroute { color: #fff; }
  .rt-nbody { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; color: #b7b9e2; min-width: 0; }
  .rt-nline { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 2px; }
  .rt-nchip { display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; background: rgba(255,255,255,.14); color: #ececff; font-size: 10.5px; font-weight: 700; padding: 4px 8px; border-radius: 8px; }
  .rt-ntimes { font-size: 14px; font-weight: 700; color: #fff; font-variant-numeric: tabular-nums; margin-top: 2px; }
  .rt-nprod { font-size: 12.5px; font-weight: 600; color: #d5d6f0; }
  .rt-nnote { font-size: 12px; font-weight: 600; color: #d5d6f0; }
  /* Narrow screens (Paulo 2026-08-24): the mode picker was squeezing the
     "origin → destination" title. The card becomes a 2×3 GRID — row 1: title
     top-left; row 2: picker + stats on one line — with the .rt-lside column
     (date, then pill/CTA) spanning both rows on the right, so the date sits
     centered over the Search-tickets button. display:contents dissolves
     .rt-linfo; its children take their own grid areas. */
  @media (max-width: 640px) {
    .rt-legpill { display: grid; grid-template-columns: auto 1fr auto; grid-template-areas: 'title title side' 'emoji stats side'; column-gap: 10px; row-gap: 2px; align-items: center; }
    .rt-linfo { display: contents; }
    .rt-lroute { grid-area: title; min-height: 0; min-width: 0; }
    .rt-lemoji { grid-area: emoji; }
    .rt-lstats { grid-area: stats; min-width: 0; margin-top: 0; }
    .rt-lside { grid-area: side; align-self: center; }
    .rt-lcta { margin-top: 0 !important; }
    .rt-legpill.has-hint { grid-template-areas: 'title title side' 'emoji stats side' 'hint hint hint'; }
    .rt-nhint { grid-area: hint; margin-top: 6px; }
    /* Night card: the train's block takes the full width under the title,
       the mode picker sits beside the last line. */
    .rt-legpill.night { grid-template-areas: 'title title side' 'nbody nbody nbody' 'emoji nstats nstats'; row-gap: 6px; }
  }
  .rt-lstats { color: #6c7872; font-size: 12.5px; margin-top: 2px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .rt-addhere { width: 24px; height: 24px; border-radius: 50%; background: #fff; border: 1.5px solid rgba(21,122,85,0.28); color: #157a55; display: grid; place-items: center; cursor: pointer; padding: 0; transition: .15s; }
  .rt-addhere:active { transform: scale(.9); }
  @media (hover: hover) { .rt-addhere:hover { background: #157a55; color: #fff; border-color: #157a55; } }
  @media (hover: hover) { .rt-stt:hover { filter: brightness(.97); } }
  /* Route text as a tap target that opens the leg detail sheet (prototype .legbtn). */
  .rt-legbtn { display: inline-flex; align-items: center; gap: 5px; min-width: 0; background: none; border: none; padding: 0; margin: 0; font: inherit; font-weight: 600; color: #16241d; text-align: left; cursor: pointer; }
  .rt-legchev { color: #b3bbb6; font-size: 15px; line-height: 1; flex: none; transition: color .12s, transform .12s; }
  @media (hover: hover) { .rt-legbtn:hover .rt-legchev { color: #157a55; transform: translateX(1px); } }
  .stop-actions, .leg-actions { opacity: 0; transition: opacity .12s ease; }
  .stoprow:hover .stop-actions, .legrow:hover .leg-actions { opacity: 1; }
  /* Touch (mobile-first): no hover — keep affordances visible but faded. */
  @media (hover: none) { .stop-actions, .leg-actions { opacity: 0.5; } .stop-actions:active, .leg-actions:active { opacity: 1; } }
  @keyframes lp-blink { 0%,80%,100% { opacity: .2 } 40% { opacity: 1 } }
  .lp-dot { animation: lp-blink 1.2s infinite; } .lp-d2 { animation-delay: .2s; } .lp-d3 { animation-delay: .4s; }
`;

const RouteTimeline: React.FC<RouteTimelineProps> = ({ stops, legs, departureDate, returnDate, stickyTop, nightsPending, busy, onAddStop, onRemoveStop, onReplaceStop, onSuggestPlaces, onPreviewPlace, onOpenLeg, onOpenStop, onBookingStop, onNightsChange, onNightsSaved, onToggleStopPlanned, onToggleLegPlanned, onOmioLeg, onNightTrainBooking, omioBusyLegId, bookingBusyStopId, onChangeLegMode, replaceRequestStopId, onReplaceRequestHandled, onSearchNightTrainsNearby, onHoverItem, selectedStopId }) => {
  const ordered = [...stops].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  // Start, End or a number per stop, the same as on the map.
  const seq = stopSequenceLabels(ordered);
  const orderedLegs = [...legs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const legBetween = (fromId?: string, toId?: string, idx?: number) =>
    orderedLegs.find((l) => l.fromStopId === fromId && l.toStopId === toId) || orderedLegs[idx as number];

  // "Nights planned" wheel: sum nights across stops (x) vs total trip nights (y).
  // Seed from the server-stored durations; overlay live stepper edits (which
  // persist on a debounce) so the wheel moves as the user changes nights.
  const [editedNights, setEditedNights] = useState<Record<string, number>>({});
  // A stop's stored hours changing means someone else persisted nights (the
  // stop SHEET's stepper, whose edits the page patches into journey state) —
  // drop this component's overlay entry so the fresh value wins instead of a
  // stale card-stepper tick from earlier in the session.
  const hoursSeenRef = useRef<Record<string, number | null | undefined>>({});
  useEffect(() => {
    stops.forEach((s) => {
      if (!s.id) return;
      const prev = hoursSeenRef.current[s.id];
      const cur = s.stopDurationHours ?? null;
      if (prev !== undefined && prev !== cur) {
        setEditedNights((e) => {
          if (e[s.id!] == null) return e;
          const { [s.id!]: _dropped, ...rest } = e;
          return rest;
        });
      }
      hoursSeenRef.current[s.id] = cur;
    });
  }, [stops]);
  const nightsFor = (s: TimelineStop) => (s.id && editedNights[s.id] != null ? editedNights[s.id] : hoursToNights(s.stopDurationHours));
  // A night on a night train is a night of the trip: it counts in the wheel.
  const nightsOnBoard = orderedLegs.reduce((sum, l) => sum + legNightsOnBoard(l), 0);
  const nightsX = ordered.reduce((sum, s) => sum + nightsFor(s), 0) + nightsOnBoard;

  // Expected schedule (shared walk; live: editedNights overlays the stored
  // nights, so stepper ticks reshuffle every downstream date immediately, and a
  // user-set ticket date re-anchors its leg + everything after it).
  const schedule = computeJourneySchedule(ordered, orderedLegs, departureDate, editedNights);
  const stopWindows: Record<string, ScheduleTag> = {};
  Object.entries(schedule.stopWindows).forEach(([id, w]) => {
    // A departure before the arrival can't be rendered as a range ("12 – 5 Jun"
    // reads as nonsense): show the known arrival and "?" for the departure.
    const label = w.toMs < w.fromMs ? `${fmtDay(w.fromMs)} – ?` : fmtDayRange(w.fromMs, w.toMs);
    stopWindows[id] = { label, conflict: !!w.conflict };
  });
  const legDays: Record<string, ScheduleTag> = {};
  Object.entries(schedule.legDays).forEach(([id, d]) => { legDays[id] = { label: fmtDay(d.ms), conflict: !!d.conflict }; });
  const nightsY = tripNights(departureDate, returnDate);
  // "Transport planned" wheel: planned legs / all legs (updates optimistically
  // via the journey state the page patches on toggle).
  const transportX = orderedLegs.filter((l) => l.planned).length;
  const transportY = orderedLegs.length;

  // A stop picked on the map scrolls its card into view: centered, so the
  // sticky bars above the timeline never cover it.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!selectedStopId) return;
    const row = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[data-stop-id]') || [])
      .find((el) => el.dataset.stopId === selectedStopId);
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    row?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  }, [selectedStopId]);

  // Hover reporting wraps each stop/leg row in a box-less element
  // (display: contents), so the rail layout is untouched.
  const hoverable = (item: TimelineHoverItem | null, children: React.ReactNode) =>
    onHoverItem && item?.id ? (
      <div style={{ display: 'contents' }} onMouseEnter={() => onHoverItem(item)} onMouseLeave={() => onHoverItem(null)}>
        {children}
      </div>
    ) : children;

  return (
    <div ref={rootRef} className={onHoverItem ? 'rt-hover-focus' : undefined}>
      <style>{RAIL_CSS}</style>
      <RouteHead nightsX={nightsX} nightsY={nightsY} nightsOnBoard={nightsOnBoard} transportX={transportX} transportY={transportY} startDate={fmtDate(departureDate)} stickyTop={stickyTop} />
      <div className="rt-track">
        <div className="rt-spine" />
        {onAddStop && <AddHere afterStopId={null} busy={busy} onAdd={onAddStop} onSuggest={onSuggestPlaces} onPreview={onPreviewPlace} />}
        {ordered.map((stop, i) => {
          const next = ordered[i + 1];
          const leg = next ? legBetween(stop.id, next.id, i) : null;
          return (
            <React.Fragment key={stop.id || i}>
              {hoverable(stop.id ? { kind: 'stop', id: stop.id } : null,
                <Stop stop={stop} seq={seq[i]} dates={stop.id ? stopWindows[stop.id] ?? null : null} busy={busy} onRemove={onRemoveStop} onReplace={onReplaceStop} onSuggestPlaces={onSuggestPlaces} onPreviewPlace={onPreviewPlace} onNightsChange={(id, n) => { setEditedNights((e) => ({ ...e, [id]: n })); onNightsChange?.(id, n); }} onNightsSaved={onNightsSaved} onTogglePlanned={onToggleStopPlanned} onOpen={onOpenStop} onBooking={onBookingStop} bookingBusy={bookingBusyStopId === stop.id} replaceRequested={replaceRequestStopId === stop.id} onReplaceRequestHandled={onReplaceRequestHandled} nightsPending={nightsPending} selected={!!stop.id && stop.id === selectedStopId} />,
              )}
              {leg && next && hoverable({ kind: 'leg', id: leg.id },
                <Leg leg={leg} from={cityOnly(stop.city || stop.location)} to={cityOnly(next.city || next.location)} fromFlag={getCountryFlag(stop.country)} toFlag={getCountryFlag(next.country)} dateLabel={legDays[leg.id] ?? null} busy={busy} onOpen={onOpenLeg} onTogglePlanned={onToggleLegPlanned} onOmio={onOmioLeg} omioBusy={omioBusyLegId === leg.id} onChangeMode={onChangeLegMode} onNightTrainBooking={onNightTrainBooking} onSearchNearby={onSearchNightTrainsNearby} />,
              )}
              {onAddStop && stop.id && <AddHere afterStopId={stop.id} busy={busy} onAdd={onAddStop} onSuggest={onSuggestPlaces} onPreview={onPreviewPlace} />}
            </React.Fragment>
          );
        })}
      </div>
      {/* "Trip ends" (was "End"): a round trip's last stop is now labelled
          End, right above, and this mirrors "Trip starts" at the top. */}
      <Anchor label="Trip ends" date={fmtDate(returnDate)} />
    </div>
  );
};

export default RouteTimeline;
